import { WeekViewSort, WeekViewSortType } from './types'

export const staticSorts: WeekViewSort[] = [
  {
    id: WeekViewSortType.None,
    type: WeekViewSortType.None,
    title: 'None',
  },
  {
    id: WeekViewSortType.Time,
    type: WeekViewSortType.Time,
    title: 'Time of Day',
  },
  {
    id: WeekViewSortType.Channel,
    type: WeekViewSortType.Channel,
    title: 'Channel',
  },
  {
    id: WeekViewSortType.Account,
    type: WeekViewSortType.Account,
    title: 'Account',
  },
]

// Single-bucket id for the `None` sort — both getters return this string.
export const NO_GROUPING_CATEGORY_ID = 'all'

// Sentinel for the default "Color By: Sort" option — the card stripe follows
// whatever the view is sorted by (today's default behavior). `type: None` is
// safe here because `colorBy` is only ever read by the stripe-color hook,
// which keys off `type`; it never feeds `mapSortField`/`mapSortPivots`. This
// is distinct from the "No Grouping" sort in `staticSorts`.
export const AUTO_COLOR_BY: WeekViewSort = {
  id: 'auto',
  title: 'Sort',
  type: WeekViewSortType.None,
}

// Zero-padded 24h id so categories sort lexicographically into
// chronological order; `formatHourLabel` is the user-facing variant.
export const formatHourId = (hour: number): string =>
  hour.toString().padStart(2, '0')

export const formatHourLabel = (hour: number): string => {
  const period = hour < 12 ? 'am' : 'pm'
  const display = hour % 12 || 12
  return `${display}${period}`
}

const mapLabelSetToSort = (labelSet: unknown): WeekViewSort | undefined => {
  if (!isValidLabelSet(labelSet)) return undefined

  return {
    id: labelSet.id,
    title: labelSet.name,
    type: WeekViewSortType.LabelSet,
  }
}

type PartialLabelSet = {
  id: string
  name: string
}

function isValidLabelSet(labelSet: unknown): labelSet is PartialLabelSet {
  return (
    labelSet !== undefined &&
    labelSet !== null &&
    typeof labelSet === 'object' &&
    typeof (labelSet as PartialLabelSet).id === 'string' &&
    typeof (labelSet as PartialLabelSet).name === 'string'
  )
}

const isValidSortOption = (
  sort: WeekViewSort | undefined
): sort is WeekViewSort => sort !== undefined

export const makeSortOptions = (labelSets: unknown[]): WeekViewSort[] => {
  const labelSetSorts = labelSets
    ?.map(mapLabelSetToSort)
    .filter(isValidSortOption)

  return labelSetSorts === undefined
    ? staticSorts
    : [...staticSorts, ...labelSetSorts]
}

export const mapSortField = (sort: WeekViewSort): string =>
  ({
    [WeekViewSortType.Time]: 'day_segment',
    [WeekViewSortType.Channel]: 'services',
    [WeekViewSortType.Account]: 'account.name',
    [WeekViewSortType.LabelSet]: sort.title,
    [WeekViewSortType.None]: 'none',
  })[sort.type]

export const mapSortPivots = (sort: WeekViewSort): string[] =>
  ({
    [WeekViewSortType.Time]: [
      'null-value',
      'morning',
      'afternoon',
      'evening',
      'night',
    ],
    [WeekViewSortType.Channel]: ['null-value'],
    [WeekViewSortType.Account]: ['null-value'],
    [WeekViewSortType.LabelSet]: ['null-value'],
    [WeekViewSortType.None]: [NO_GROUPING_CATEGORY_ID],
  })[sort.type]
